import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BsCheckCircleFill } from 'react-icons/bs';
import { FiShoppingCart } from 'react-icons/fi';
import {
  SecondaryButton,
  PrimaryButton,
} from '../../../../../shared/ui/Inputs';
import { Panel } from '../../../../../shared/ui/panel/panels';
import { ProductImage } from '../ProductImage';
import { useOrderFlowStore } from '../../../store/newOrderFlowStore';
import { useLegacyQuestionnaire } from './useLegacyQuestionnaire';
import { getProductsByCodes } from '../../../../../services/products/productService';

const GREEN = '#318300';
const GREEN_LIGHT = '#f0f7eb';
const GREEN_BORDER = '#318300';
const DIVIDER = '1px solid #f0f0f0';

const ReadyMessage = () => (
  <div
    style={{
      padding: '24px 20px 16px',
      borderBottom: DIVIDER,
      textAlign: 'center',
    }}
  >
    <BsCheckCircleFill
      style={{ fontSize: '36px', color: GREEN, marginBottom: '10px' }}
    />
    <p
      style={{
        margin: 0,
        fontSize: '14px',
        fontWeight: '600',
        color: '#222',
        lineHeight: '1.6',
      }}
    >
      Pronto! Confira seus acessórios e prossiga para o agendamento.
    </p>
  </div>
);

// ─── Barra de progresso ────────────────────────────────────────────────────────
const ProgressBar = ({ current, total }) => {
  const safeTotal = Math.max(total, current, 1);
  const pct = Math.min((current / safeTotal) * 100, 100);
  return (
    <div style={{ padding: '16px 20px 12px' }}>
      <div
        style={{
          marginBottom: '8px',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '600', color: GREEN }}>
          Etapa {current} de {safeTotal}
        </span>
      </div>
      <div
        style={{
          width: '100%',
          height: '6px',
          backgroundColor: '#e8e8e8',
          borderRadius: '99px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: GREEN,
            borderRadius: '99px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
};

// ─── Card de opção ─────────────────────────────────────────────────────────────
const OptionCard = ({ option, checked, onChange }) => {
  return (
    <div
      role="radio"
      aria-checked={checked}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onChange();
        }
      }}
      onClick={onChange}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        marginBottom: '8px',
        borderRadius: '8px',
        border: checked ? `2px solid ${GREEN_BORDER}` : '2px solid #e0e0e0',
        backgroundColor: checked ? GREEN_LIGHT : '#fff',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      {/* Indicador visual */}
      <div
        style={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          border: checked ? `5px solid ${GREEN}` : '2px solid #ccc',
          backgroundColor: '#fff',
          flexShrink: 0,
          transition: 'border 0.15s',
        }}
      />
      <span
        style={{
          fontSize: '13px',
          fontWeight: checked ? '600' : '400',
          color: checked ? GREEN : '#333',
          lineHeight: '1.4',
        }}
      >
        {option}
      </span>
    </div>
  );
};

// ─── Lista de acessórios ───────────────────────────────────────────────────────
const AccessoryList = ({
  listOfProd,
  prodsToShow,
  fullListProd,
  compact = false,
}) => {
  if (listOfProd.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        padding: compact ? '10px 20px' : '14px 20px',
        borderTop: DIVIDER,
        backgroundColor: '#fafafa',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '10px',
        }}
      >
        <FiShoppingCart style={{ color: GREEN, fontSize: '14px' }} />
        <span
          style={{
            fontSize: '12px',
            fontWeight: '700',
            color: '#444',
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
          }}
        >
          Acessórios selecionados
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {listOfProd.map((prodId, k) => (
          <div
            key={k}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: '#333',
            }}
          >
            <span
              style={{
                minWidth: '28px',
                padding: '1px 6px',
                borderRadius: '20px',
                backgroundColor: GREEN_LIGHT,
                color: GREEN,
                fontSize: '11px',
                fontWeight: '700',
                textAlign: 'center',
              }}
            >
              {prodsToShow[prodId].qty}x
            </span>
            <span style={{ lineHeight: '1.4' }}>
              {fullListProd[prodId]?.description}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Rodapé de navegação ───────────────────────────────────────────────────────
const NavFooter = ({ onBack, onNext, showBack, showNext }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 20px',
      borderTop: DIVIDER,
    }}
  >
    <div>
      {showBack && (
        <SecondaryButton
          type="button"
          onClick={onBack}
          style={{ minWidth: '90px' }}
        >
          Voltar
        </SecondaryButton>
      )}
    </div>
    <div>
      {showNext && (
        <PrimaryButton
          type="button"
          onClick={onNext}
          style={{ minWidth: '110px' }}
        >
          Prosseguir
        </PrimaryButton>
      )}
    </div>
  </div>
);

// ─── Utilitário: montar lista de produtos ──────────────────────────────────────
const buildProductList = (currentAnswers, fullListProd) => {
  const listOfProd = [];
  const prodsToShow = {};
  const prdToShow = Object.keys(currentAnswers)
    .map((x) => currentAnswers[x].productsToAdd)
    .flat()
    .filter((prodId) => fullListProd[prodId] !== undefined);

  prdToShow.forEach((prod) => {
    if (listOfProd.includes(prod)) {
      prodsToShow[prod].qty += 1;
    } else {
      listOfProd.push(prod);
      prodsToShow[prod] = { product: prod, qty: 1 };
    }
  });

  return { listOfProd, prodsToShow };
};

const calculateDynamicTotalSteps = (
  questionPath,
  firstQuestion,
  currentQuestion,
  currentAnswers,
  stepNumber
) => {
  if (!firstQuestion || !currentQuestion) {
    return Math.max(stepNumber, 1);
  }

  const questionById = questionPath.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

  const getPossibleNextIds = (question) => {
    if (!question) return [];

    const selectedAnswer = currentAnswers[question.id];
    if (selectedAnswer?.nextId != null) {
      return [selectedAnswer.nextId].filter((id) => questionById[id]);
    }

    const nextIds = [];
    (question.options ?? []).forEach((opt) => {
      const answer = opt?.answers?.[0];
      const nextStep = answer?.next_step;
      if (nextStep == null) return;
      const nextQuestion = questionPath.find((x) => x.id === nextStep);
      if (nextQuestion) {
        nextIds.push(nextQuestion.id);
      }
    });

    if (
      nextIds.length === 0 &&
      question.next?.id &&
      questionById[question.next.id]
    ) {
      nextIds.push(question.next.id);
    }

    return Array.from(new Set(nextIds));
  };

  const getMinimumEstimatedDepth = (questionId, visited = new Set()) => {
    if (!questionById[questionId] || visited.has(questionId)) return 0;

    const nextVisited = new Set(visited);
    nextVisited.add(questionId);

    const selectedNextId = currentAnswers[questionId]?.nextId;
    if (selectedNextId && questionById[selectedNextId]) {
      return 1 + getMinimumEstimatedDepth(selectedNextId, nextVisited);
    }

    const nextIds = getPossibleNextIds(questionById[questionId]);
    if (nextIds.length === 0) {
      return 1;
    }

    let minimumChildDepth = Number.POSITIVE_INFINITY;
    nextIds.forEach((nextId) => {
      minimumChildDepth = Math.min(
        minimumChildDepth,
        getMinimumEstimatedDepth(nextId, nextVisited)
      );
    });

    if (!Number.isFinite(minimumChildDepth)) {
      return 1;
    }

    return 1 + minimumChildDepth;
  };

  const getDistanceFromStart = () => {
    const visited = new Set();
    let cursor = firstQuestion;
    let distance = 1;

    while (
      cursor &&
      cursor.id !== currentQuestion.id &&
      !visited.has(cursor.id)
    ) {
      visited.add(cursor.id);
      const nextId = currentAnswers[cursor.id]?.nextId;
      if (!nextId || !questionById[nextId]) {
        break;
      }
      cursor = questionById[nextId];
      distance += 1;
    }

    return distance;
  };

  const remainingFromCurrent = getMinimumEstimatedDepth(currentQuestion.id);
  const progressed = getDistanceFromStart();

  const estimatedTotal = progressed + Math.max(remainingFromCurrent - 1, 0);

  return Math.max(estimatedTotal, stepNumber, 1);
};

// ─── Componente principal ──────────────────────────────────────────────────────
const OrderConfigureTile = ({ width, style, questions }) => {
  const [stepNumber, setStepNumber] = useState(1);

  questions.sort((a, b) => a.step - b.step);
  let first = questions.find((x) => x.step === 1) ?? questions[0];

  const [questionPath] = useState(questions);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(first);
  const [currentAnswers, setCurrentAnswers] = useState({});
  const [finishQuestions, setFinishQuestions] = useState(
    questionPath.length === 0
  );

  const setConfiguredEquipment = useOrderFlowStore(
    (state) => state.setConfiguredEquipment
  );
  const setFullListProd = useOrderFlowStore((state) => state.setFullListProd);
  const productPriorityMap = useOrderFlowStore(
    (state) => state.productPriorityMap
  );
  const currentPatientBranch = useOrderFlowStore(
    (state) => state.currentPatientBranch
  );
  const fullListProd = useOrderFlowStore((state) => state.fullListProd);
  const navigate = useNavigate();

  const totalSteps = calculateDynamicTotalSteps(
    questionPath,
    first,
    currentQuestion,
    currentAnswers,
    stepNumber
  );

  // ── Lógica de navegação (inalterada) ────────────────────────────────────────

  const getNextStepCalc = (question, idx) => {
    let action = question.options[idx].answers[0];
    if (!action) {
      action = { product_id: [], next_step: currentQuestionIdx + 1 };
    }
    let nextStepCalc = 999;
    const foundStep = (questionPath.filter((x) => x.id === action.next_step) ??
      [])[0];
    if (foundStep) {
      nextStepCalc = foundStep.step;
      return { nextStepCalc, nextId: foundStep.id };
    }
    return { nextStepCalc, nextId: null };
  };

  const checkInventory = (question, answers) => {
    const map = {};
    const productsChart = Object.values(answers)
      .map((x) => x.productsToAdd)
      .flat();
    for (const p of productsChart) map[p] = true;

    for (const option of question.options) {
      const itemsToCheck = option.option
        .split(',')
        .map((o) => parseInt(o.trim()));
      let ok = itemsToCheck.every((item) => map[item] === true);
      if (ok) {
        const nextStepCalc = (questionPath.filter(
          (x) => x.id === option.answers[0].next_step
        ) ?? [])[0];
        answers[question.id] = {
          value: itemsToCheck,
          productsToAdd: option.answers[0].product_id,
          nextStep: nextStepCalc.step ?? 999,
          nextId: nextStepCalc.id,
          previousStep: question.step,
          previousId: question.id,
        };
        return nextStepCalc;
      }
    }
    return { step: 999 };
  };

  const onCheckOption = (question, idx, value) => {
    let action = question.options[idx].answers[0];
    if (!action) action = { product_id: [], next_step: currentQuestionIdx + 1 };

    let obj = getNextStepCalc(question, idx);

    currentAnswers[question.id] = {
      questionId: question.id,
      value,
      productsToAdd: question.options[idx].answers
        .map((x) => x.product_id)
        .flat()
        .map((p) => {
          if (productPriorityMap[p]?.[currentPatientBranch.id]) {
            return productPriorityMap[p][currentPatientBranch.id].use;
          }
          return null;
        })
        .filter((x) => x !== null),
      nextStep: obj.nextStepCalc,
      nextId: obj.nextId,
      previousStep: question.step,
      previousId: question.id,
    };

    const nextQuestion = questionPath.find((x) => x.id === obj.nextId);
    if (
      nextQuestion &&
      nextQuestion.options.filter((o) => o.option_type === 'i').length > 0
    ) {
      const nextStep = checkInventory(nextQuestion, currentAnswers);
      let next = questionPath.find((x) => x.step === parseInt(nextStep));
      if (nextStep.step === 999) next = null;
      if (next) {
        next.previous = currentQuestion;
        setCurrentQuestion(JSON.parse(JSON.stringify(next)));
        setCurrentQuestionIdx(parseInt(nextStep));
      } else {
        setFinishQuestions(true);
      }
    } else {
      setCurrentQuestionIdx(obj.nextId);
    }
    setCurrentAnswers(JSON.parse(JSON.stringify(currentAnswers)));
  };

  const getNextQuestion = () => {
    if (currentQuestion.next) {
      setCurrentQuestion(JSON.parse(JSON.stringify(currentQuestion.next)));
      setCurrentQuestionIdx(currentQuestion.next.id);
      return;
    }
    let q = questionPath.find((x) => x.id === currentQuestionIdx);
    if (q && q.options.length > 0) {
      if (q.id === currentQuestionIdx && currentAnswers[q.id]) {
        const foundStep =
          questionPath.find((x) => x.id === currentAnswers[q.id].questionId) ??
          {};
        const opt = foundStep.options.findIndex(
          (x) => x.option === currentAnswers[q.id].value
        );
        const value = currentAnswers[q.id].value;
        onCheckOption(foundStep, opt, value);
        const nextQ = getNextStepCalc(foundStep, opt);
        const nextQuestion = questionPath.find((x) => x.id === nextQ.nextId);
        setCurrentQuestion(JSON.parse(JSON.stringify(nextQuestion)));
      } else {
        q.previous = currentQuestion;
        setCurrentQuestion(JSON.parse(JSON.stringify(q)));
        setCurrentQuestionIdx(q.id);
      }
    } else {
      q = { id: currentQuestion.id, previous: currentQuestion };
      setCurrentQuestion(JSON.parse(JSON.stringify(q)));
      setCurrentQuestionIdx(q.id);
      setFinishQuestions(true);
    }
  };

  const moveNext = () => {
    setStepNumber(stepNumber + 1);
    getNextQuestion();
  };

  const moveBack = () => {
    if (!currentQuestion?.previous) return;
    setStepNumber(stepNumber - 1);
    delete currentAnswers[currentQuestion.id];
    let question = currentQuestion;
    while (question.next) {
      delete currentAnswers[currentQuestion.next.id];
      question = question.next;
    }
    setCurrentAnswers(JSON.parse(JSON.stringify(currentAnswers)));
    const prev = JSON.parse(JSON.stringify(currentQuestion.previous ?? {}));
    prev.next = null;
    setCurrentQuestionIdx(currentQuestion.previous.id);
    setCurrentQuestion(prev);
    setFinishQuestions(false);
  };

  const prepareOrder = async () => {
    const productCodes = [
      ...new Set(
        Object.values(currentAnswers)
          .flatMap((answer) => answer.productsToAdd ?? [])
          .filter((code) => code !== undefined && code !== null && code !== '')
          .map((code) => Number(code))
          .filter(Number.isFinite)
      ),
    ];
    const products = await getProductsByCodes(productCodes);
    const productsByCode = products.reduce((map, product) => {
      if (product?.sap_code !== undefined && product?.sap_code !== null) {
        map[product.sap_code] = product;
      }
      return map;
    }, {});

    setConfiguredEquipment(currentAnswers);
    setFullListProd(productsByCode);
    navigate('/order/finish');
  };

  // ── Render: formulário de perguntas ─────────────────────────────────────────

  const configForm = () => {
    const { listOfProd, prodsToShow } = buildProductList(
      currentAnswers,
      fullListProd
    );
    const hasAnswer = !!currentAnswers[currentQuestion.id];
    const showBack = currentQuestion.step < 999 && currentQuestion.step > 1;
    const showNext = currentQuestion.step < 999 && hasAnswer;

    return (
      <>
        <ProgressBar current={stepNumber} total={totalSteps} />

        {/* Pergunta */}
        <div style={{ padding: '0 20px 16px', borderBottom: DIVIDER }}>
          <p
            style={{
              margin: '0 0 14px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#222',
              lineHeight: '1.5',
            }}
          >
            {currentQuestion.question}
          </p>

          {/* Opções como cards */}
          <div>
            {(currentQuestion.options ?? [])
              .filter((o) => o.option_type !== 'i')
              .map((opt, idx) => (
                <OptionCard
                  key={idx}
                  option={opt.option}
                  checked={
                    currentAnswers[currentQuestion.id]?.value === opt.option
                  }
                  onChange={() =>
                    onCheckOption(currentQuestion, idx, opt.option)
                  }
                />
              ))}
          </div>
        </div>

        {/* Navegação */}
        <NavFooter
          onBack={moveBack}
          onNext={moveNext}
          showBack={showBack}
          showNext={showNext}
        />

        {/* Imagem e acessórios ficam abaixo dos botões para evitar variação de layout */}
        <div style={{ borderTop: DIVIDER }}>
          <div style={{ padding: '8px 20px 0' }}>
            <ProductImage
              hideNoImage={true}
              maxWidth="50px"
              codes={
                ((currentAnswers ?? {})[currentQuestion.id] ?? {})
                  .productsToAdd ?? []
              }
            />
          </div>

          <AccessoryList
            listOfProd={listOfProd}
            prodsToShow={prodsToShow}
            fullListProd={fullListProd}
            compact
          />
        </div>
      </>
    );
  };

  // ── Render: tela final ───────────────────────────────────────────────────────

  const finalForm = () => {
    const { listOfProd, prodsToShow } = buildProductList(
      currentAnswers,
      fullListProd
    );

    return (
      <>
        {/* Mensagem de conclusão */}
        <ReadyMessage />

        {/* Navegação */}
        <NavFooter
          onBack={moveBack}
          onNext={prepareOrder}
          showBack={!!currentQuestion?.previous}
          showNext={true}
        />

        {/* Lista final de acessórios abaixo dos botões */}
        <AccessoryList
          listOfProd={listOfProd}
          prodsToShow={prodsToShow}
          fullListProd={fullListProd}
        />
      </>
    );
  };

  return (
    <Panel
      width={width ?? '30%'}
      style={{
        ...style,
        position: 'relative',
        marginLeft: '0',
        marginRight: '10px',
      }}
    >
      {finishQuestions || questions.length === 0 ? finalForm() : configForm()}
    </Panel>
  );
};

/** Modo incorporado: estado local, perguntas editáveis e nenhuma finalização de pedido. */
const EmbeddedConfigureTile = ({
  questions,
  onAnswersChange,
  initialProductCode,
  onConfigurationChange,
}) => {
  const q = useLegacyQuestionnaire(
    questions,
    onAnswersChange,
    initialProductCode
  );
  useEffect(() => {
    onConfigurationChange?.({ answers: q.answers, complete: q.complete });
  }, [q.answers, q.complete, onConfigurationChange]);
  return (
    <Panel
      width="100%"
      style={{ border: '1px solid #dce5de', boxShadow: 'none', minWidth: 0 }}
    >
      {q.invalid && (
        <p role="alert" style={{ padding: '12px 20px', color: '#b3261e' }}>
          Não foi possível resolver a sequência de perguntas deste produto.
        </p>
      )}
      {!q.current ? (
        !q.invalid && <ReadyMessage />
      ) : (
        <>
          <ProgressBar
            current={q.position + 1}
            total={Math.max(q.path.length, q.position + 1)}
          />
          <div style={{ padding: '0 20px 16px' }}>
            <p style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600 }}>
              {q.current.question}
            </p>
            <div role="radiogroup" aria-label={q.current.question}>
              {q.current.options.map((option, index) =>
                option.option_type === 'i' ? null : (
                  <OptionCard
                    key={index}
                    option={option.option}
                    checked={q.choices[String(q.current.id).trim()] === index}
                    onChange={() => q.answer(index)}
                  />
                )
              )}
            </div>
          </div>
          <NavFooter
            onBack={q.back}
            onNext={q.next}
            showBack={q.canBack}
            showNext={q.canNext}
          />
        </>
      )}
    </Panel>
  );
};

/**
 * @param {{ width?: string, style?: object, questions: import('./useLegacyQuestionnaire').LegacyQuestion[], embedded?: boolean, initialProductCode?: string, onAnswersChange?: (answers: import('./useLegacyQuestionnaire').LegacyAnswers) => void, onConfigurationChange?: (value: { answers: import('./useLegacyQuestionnaire').LegacyAnswers, complete: boolean }) => void }} props
 */
export const ConfigureTile = ({
  embedded = false,
  onAnswersChange,
  initialProductCode,
  onConfigurationChange,
  ...props
}) =>
  embedded ? (
    <EmbeddedConfigureTile
      questions={props.questions}
      onAnswersChange={onAnswersChange}
      initialProductCode={initialProductCode}
      onConfigurationChange={onConfigurationChange}
    />
  ) : (
    <OrderConfigureTile {...props} />
  );
